import { FC } from 'react';
import { CircularProgress } from '@mui/material';
import { ClassroomMeetingType, DateRangeValue } from '../../utils/interface';
import { useClassroomMeetings } from '../../hooks/useClassroomMeetings';
import { useInfiniteScrollTrigger } from '../../hooks/useInfiniteScrollTrigger';
import ToasterWithMessage from '../toaster';
import Loader from '../Loader/Loader';
import AttendanceCard from '../AttendanceCard/AttendanceCard';
import './AttendanceTab.scss';

interface AttendanceTabProps {
  classroomId?: string | null;
  subjectId?: string | null;
  /** Date range chosen in the header filter. */
  range: DateRangeValue;
  /** Opens the attendance report of a meeting */
  onOpenMeeting?: (meeting: ClassroomMeetingType) => void;
}

const AttendanceTab: FC<AttendanceTabProps> = ({ classroomId, subjectId, range, onOpenMeeting }) => {
  const { error } = ToasterWithMessage();
  const { meetings, loading, loadingMore, hasError, hasMore, fetchMore } = useClassroomMeetings({
    classroomId,
    subjectId,
    range,
    onError: error,
  });

  // Loads the next page whenever the bottom of the list is in view, even if the list is too short to scroll.
  // The viewport is used as the observer root, so it works whichever ancestor ends up scrolling.
  const sentinelRef = useInfiniteScrollTrigger({
    onLoadMore: fetchMore,
    hasMore,
    isLoadingMore: loadingMore,
    itemCount: meetings.length,
  });

  return (
    <div className='attendanceTabContainer'>
      {loading ? (
        <Loader bottom='130px' />
      ) : meetings.length > 0 ? (
        <>
          <div className='attendanceCardGrid'>
            {meetings.map((meeting) => (
              <AttendanceCard key={meeting.sessionId} meeting={meeting} onOpen={onOpenMeeting} />
            ))}
          </div>
          {hasMore && (
            <div className='attendanceScrollLoader' ref={sentinelRef}>
              {loadingMore && <CircularProgress size={30} />}
            </div>
          )}
        </>
      ) : (
        <div className='attendanceEmptyState'>
          {hasError ? 'Unable to load meetings. Please try again.' : 'No meetings found for the selected date range.'}
        </div>
      )}
    </div>
  );
};

export default AttendanceTab;
